/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Mail, Phone, Calendar as CalIcon, DollarSign, MessageSquare, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { key: "all", label: "All" },
  { key: "contact", label: "Contact" },
  { key: "test_drive", label: "Test Drives" },
  { key: "financing", label: "Financing" },
  { key: "interest", label: "Interested" },
];

const replyDraft = (it) => {
  const first = (it.name || "").trim().split(" ")[0];
  const about =
    it.type === "test_drive" ? " about scheduling a test drive" :
    it.type === "financing" ? " about financing" :
    it.type === "interest" ? " about the vehicle you're interested in" : "";
  return `Hi ${first}, this is Pit Stop Motors — thanks for reaching out${about}. `;
};

const smsHref = (phone, body) => `sms:${phone.replace(/[^\d+]/g, "")}?body=${encodeURIComponent(body)}`;

export default function AdminInquiries() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { type: filter } : {};
      const r = await api.get("/inquiries", { params });
      setItems(r.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/inquiries/${id}/status`, { status });
      toast.success("Status updated");
      window.dispatchEvent(new Event("psm-inquiries-changed"));
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-inquiries">
      <h2 className="font-serif text-3xl text-primary">Inquiries</h2>
      <p className="text-sm text-muted-foreground">Messages and requests from neighbors.</p>

      <Tabs value={filter} onValueChange={setFilter} className="mt-6">
        <TabsList className="bg-secondary rounded-sm">
          {TYPES.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="rounded-sm" data-testid={`inquiry-filter-${t.key}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={filter} className="mt-6 space-y-3" data-testid="inquiry-list">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && items.length === 0 && (
            <div className="border border-dashed border-soft p-12 text-center text-muted-foreground" data-testid="inquiry-empty">
              <MessageSquare className="h-6 w-6 mx-auto opacity-40" />
              <p className="mt-3 font-serif text-xl text-primary">No inquiries yet.</p>
              <p className="mt-1 text-sm">When customers reach out, they'll appear here.</p>
            </div>
          )}
          {items.map((it) => (
            <div key={it.id} className="border border-soft bg-card p-5" data-testid={`inquiry-row-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-primary">{it.name}</span>
                    <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider">
                      {it.type.replace("_", " ")}
                    </Badge>
                    <Badge
                      className={`rounded-sm uppercase text-[10px] tracking-wider ${
                        it.status === "new" ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {it.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(it.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {it.email}</span>
                    {it.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {it.phone}</span>}
                    {it.preferred_date && <span className="inline-flex items-center gap-1"><CalIcon className="h-3 w-3" /> {it.preferred_date}</span>}
                    {it.desired_amount && <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" /> ${Number(it.desired_amount).toLocaleString()}</span>}
                    {it.credit_range && <span>Credit: {it.credit_range}</span>}
                  </div>
                  {it.message && <p className="mt-3 text-sm text-primary leading-relaxed">{it.message}</p>}
                </div>
                <div className="flex flex-col gap-2 w-36">
                  {it.phone && (
                    <Button size="sm" asChild className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm gap-1" data-testid={`text-back-${it.id}`}>
                      <a href={smsHref(it.phone, replyDraft(it))}>
                        <MessageSquare className="h-3 w-3" /> Text Back
                      </a>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" asChild className="rounded-sm gap-1" data-testid={`email-back-${it.id}`}>
                    <a href={`mailto:${it.email}?subject=${encodeURIComponent("Pit Stop Motors — your inquiry")}&body=${encodeURIComponent(replyDraft(it))}`}>
                      <Mail className="h-3 w-3" /> Email Back
                    </a>
                  </Button>
                  {it.phone && (
                    <Button size="sm" variant="outline" asChild className="rounded-sm gap-1 border-gold/60" data-testid={`review-ask-${it.id}`}>
                      <a href={smsHref(it.phone, `Thanks for choosing Pit Stop Motors, ${(it.name || "").trim().split(" ")[0]}! If you had a good experience with us, would you mind leaving a quick Google review? It means the world to a small family lot: ${BRAND.googleReviewUrl}`)}>
                        <Star className="h-3 w-3 text-gold fill-gold" /> Ask for Review
                      </a>
                    </Button>
                  )}
                  {it.status === "new" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(it.id, "responded")} data-testid={`mark-responded-${it.id}`}>
                      Mark Responded
                    </Button>
                  )}
                  {it.status !== "closed" && (
                    <Button size="sm" variant="ghost" onClick={() => updateStatus(it.id, "closed")} data-testid={`close-inquiry-${it.id}`}>
                      Close
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-inquiry-${it.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
